import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import Select from 'react-select';
import { debounce } from 'lodash';
import { RotatingLines } from "react-loader-spinner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
//import "./CourseSubscription.css";

export default function CourseSubscription() {
  // State management
  const [state, setState] = useState({
    subscriptions: [],
    courses: [],
    schools: [],
    classes: [],
    loading: true,
    formSubmitting: false,
    formData: {
      courseSubscriptionId: 0,
      courseId: 0,
      numberOfCourseContentVisible: 0,
      schoolId: 0,
      classId: 0,
      isTrialExpired: false,
      isPaymentDone: false,
      subscriptionStartDate: new Date(),
      subscrptionEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
    },
    searchQuery: "",
    filterStatus: "all",
    filterCourse: "all",
    filterSchool: "all",
    filterClass: "all",
    currentPage: 0,
    itemsPerPage: 5
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchCourses(),
          fetchSchools(),
          fetchClasses(),
          fetchSubscriptions()
        ]);
      } catch (error) {
        console.error("Error in initial data loading:", error);
      }
    };
    
    fetchData();
  }, []);
  
  // API calls
  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}Course/SuperAdminCourseMasterList`, {
        headers: getHeaders(),
      });
      updateState({ courses: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching courses:", error);
      Swal.fire("Error!", "Failed to load courses.", "error");
      throw error;
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}school/SchoolMasterList`, {
        headers: getHeaders(),
      });
      updateState({ schools: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching schools:", error);
      Swal.fire("Error!", "Failed to load schools.", "error");
      throw error;
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}class/ClassMasterList`, {
        headers: getHeaders(),
      });
      updateState({ classes: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching classes:", error);
      Swal.fire("Error!", "Failed to load classes.", "error");
      throw error;
    }
  };

  const fetchSubscriptions = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(`${API_LoginURL}CourseSubscription/CourseSubscriptionList`, {
        headers: getHeaders(),
      });
      updateState({
        subscriptions: Array.isArray(response.data) ? response.data : [],
        loading: false
      });
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      Swal.fire("Error!", "Failed to load subscriptions.", "error");
      updateState({ loading: false });
      throw error;
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token")
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: value === '' ? '' : Number(value)
      }
    });
  };

  const handleSelectChange = (selectedOption, field) => {
    updateState({
      formData: {
        ...state.formData,
        [field]: selectedOption ? selectedOption.value : 0
      }
    });
  };

  const handleDateChange = (date, field) => {
    updateState({
      formData: {
        ...state.formData,
        [field]: date
      }
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      updateState({ searchQuery: query, currentPage: 0 });
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      [e.target.name]: e.target.value,
      currentPage: 0
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        courseSubscriptionId: 0,
        courseId: 0,
        numberOfCourseContentVisible: 0,
        schoolId: 0,
        classId: 0,
        isTrialExpired: false,
        isPaymentDone: false,
        subscriptionStartDate: new Date(),
        subscrptionEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
      }
    });
  };

  const handleEdit = (subscription) => {
    updateState({
      formData: {
        ...subscription,
        subscriptionStartDate: new Date(subscription.subscriptionStartDate),
        subscrptionEndDate: new Date(subscription.subscrptionEndDate)
      }
    });
  };

  const handleDelete = async (courseSubscriptionId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.post(
        `${API_LoginURL}CourseSubscription/DeleteCourseSubscription`,
        { courseSubscriptionId },
        { headers: getHeaders() }
      );
      updateState({
        subscriptions: state.subscriptions.filter(item => item.courseSubscriptionId !== courseSubscriptionId)
      });
      Swal.fire("Deleted!", "Subscription has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting subscription:", error);
      Swal.fire("Error", "Failed to delete subscription", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    
    updateState({ formSubmitting: true });
    try {
      const response = await axios.post(
        `${API_LoginURL}CourseSubscription/CourseSubscriptionAddEdit`,
        state.formData,
        { headers: getHeaders() }
      );

      Swal.fire({
        icon: response.data.response === "SUCCESS" ? "success" : "error",
        title: response.data.response === "SUCCESS" ? "Success" : "Error",
        text: response.data.response === "SUCCESS"
          ? "Subscription saved successfully"
          : response.data.response,
      });

      if (response.data.response === "SUCCESS") {
        fetchSubscriptions();
        document.getElementById("closeSubscriptionModal").click();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving subscription:", error);
      Swal.fire("Error", "Failed to save subscription", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  const validateForm = () => {
    const { courseId, schoolId, classId, subscriptionStartDate, subscrptionEndDate } = state.formData;
    
    if (!courseId || !schoolId || !classId) {
      Swal.fire("Validation Error", "Please select Course, School and Class", "error");
      return false;
    }
    
    if (subscrptionEndDate <= subscriptionStartDate) {
      Swal.fire("Validation Error", "End date must be after start date", "error");
      return false;
    }
    
    return true;
  };
  // Helper functions to get names for display
  const getCourseName = (courseId) => {
    const course = state.courses.find(c => c.courseId === courseId);
    return course ? course.courseName : "N/A";
  };

  const getSchoolName = (schoolId) => {
    const school = state.schools.find(s => s.school_id === schoolId);
    return school ? school.school_name : "N/A";
  };

  const getClassName = (classId) => {
    const cls = state.classes.find(c => c.classId === classId);
    return cls ? cls.className : "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (subscription) => {
    if (subscription.isPaymentDone) {
      return <span className="bg-success">Paid</span>;
    } else if (subscription.isTrialExpired) {
      return <span className="bg-danger">Expired</span>;
    } else {
      return <span className="bg-warning text-dark">Trial</span>;
    }
  };
  // Filter and pagination
  const filteredSubscriptions = state.subscriptions.filter(item => {
    const matchesSearch =
      getCourseName(item.courseId)?.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      getSchoolName(item.school_id).toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      getClassName(item.classId).toLowerCase().includes(state.searchQuery.toLowerCase());

    const matchesCourse =
      state.filterCourse === "all" ||
      item.courseId.toString() === state.filterCourse;

    const matchesSchool =
      state.filterSchool === "all" ||
      item.schoolId.toString() === state.filterSchool;

    const matchesClass =
      state.filterClass === "all" ||
      item.classId.toString() === state.filterClass;

    const matchesStatus =
      state.filterStatus === "all" ||
      (state.filterStatus === "paid" && item.isPaymentDone) ||
      (state.filterStatus === "trial" && !item.isPaymentDone && !item.isTrialExpired) ||
      (state.filterStatus === "expired" && item.isTrialExpired);

    return matchesSearch && matchesCourse && matchesSchool && matchesClass && matchesStatus;
  });

  const pageCount = Math.ceil(filteredSubscriptions.length / state.itemsPerPage);
  const displayedSubscriptions = filteredSubscriptions.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );
  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">Course Subscription Management</h4>

                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Course, School or Class"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>

                      {/* Filters */}
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Course Filter */}
                        <div className="d-flex align-items-center">
                          <i className="fas fa-book me-2 text-muted"></i>
                          <select
                            className="form-select"
                            name="filterCourse"
                            value={state.filterCourse}
                            onChange={handleFilterChange}
                            style={{ minWidth: '120px' }}
                          >
                            <option value="all">All Courses</option>
                            {state.courses.map(course => (
                              <option key={course.courseId} value={course.courseId}>
                                {course.courseName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* School Filter */}
                        <div className="d-flex align-items-center">
                          <i className="fas fa-school me-2 text-muted"></i>
                          <select
                            className="form-select"
                            name="filterSchool"
                            value={state.filterSchool}
                            onChange={handleFilterChange}
                            style={{ minWidth: '120px' }}
                          >
                            <option value="all">All Schools</option>
                            {state.schools.map(school => (
                              <option key={school.school_id} value={school.school_id}>
                                {school.school_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Class Filter */}
                        <div className="d-flex align-items-center">
                          <i className="fas fa-users me-2 text-muted"></i>
                          <select
                            className="form-select"
                            name="filterClass"
                            value={state.filterClass}
                            onChange={handleFilterChange}
                            style={{ minWidth: '120px' }}
                          >
                            <option value="all">All Classes</option>
                            {state.classes.map(cls => (
                              <option key={cls.classId} value={cls.classId}>
                                {cls.className}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Status Filter */}
                        <div className="d-flex align-items-center">
                          <i className="fas fa-filter me-2 text-muted"></i>
                          <select
                            className="form-select"
                            name="filterStatus"
                            value={state.filterStatus}
                            onChange={handleFilterChange}
                            style={{ minWidth: '120px' }}
                          >
                            <option value="all">All Status</option>
                            <option value="paid">Paid</option>
                            <option value="trial">Trial</option>
                            <option value="expired">Expired</option>
                          </select>
                        </div>
                      </div>

                      {/* Add Subscription Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#subscriptionModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Subscription</span>
                      </button>
                    </div>
                  </div>

                  {/* Subscriptions Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <RotatingLines color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading subscriptions...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped">
                          <thead className="table-primary">
                            <tr>
                              <th width="5%">ID</th>
                              <th width="15%">Course</th>
                              <th width="15%">School</th>
                              <th width="10%">Class</th>
                              <th width="10%">Visible Content</th>
                              <th width="10%">Start Date</th>
                              <th width="10%">End Date</th>
                              <th width="10%">Status</th>
                              <th width="15%" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedSubscriptions.length > 0 ? (
                              displayedSubscriptions.map((item) => (
                                <tr key={item.courseSubscriptionId}>
                                  <td>{item.courseSubscriptionId}</td>
                                  <td>{getCourseName(item.courseId)}</td>
                                  <td>{getSchoolName(item.schoolId)}</td>
                                  <td>{getClassName(item.classId)}</td>
                                  <td>{item.numberOfCourseContentVisible}</td>
                                  <td>{formatDate(item.subscriptionStartDate)}</td>
                                  <td>{formatDate(item.subscrptionEndDate)}</td>
                                  <td>{getStatusBadge(item)}</td>
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#subscriptionModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.courseSubscriptionId)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No subscriptions"
                                      style={{ opacity: 0.5, width: '60px' }}
                                    />
                                    <p className="mt-3 text-muted">No subscriptions found</p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#subscriptionModal"
                                      onClick={resetForm}
                                    >
                                      Add New Subscription
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedSubscriptions.length > 0 && (
                          <ReactPaginate
                            previousLabel={<i className="fas fa-chevron-left"></i>}
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination justify-content-center mt-4"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      <div className="modal fade" id="subscriptionModal" tabIndex="-1" aria-labelledby="subscriptionModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="subscriptionModalLabel">
                {state.formData.courseSubscriptionId > 0 ? 'Edit Subscription' : 'Add New Subscription'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row decrease_gap">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="courseId" className="form-label required">Course</label>
                    <Select
                      id="courseId"
                      options={state.courses.map(course => ({
                        value: course.courseId,
                        label: `${course.courseName}`
                      }))}
                      value={state.courses.find(course => course.courseId === state.formData.courseId) ? {
                        value: state.formData.courseId,
                        label: state.courses.find(course => course.courseId === state.formData.courseId).courseName
                      } : null}
                      onChange={(selected) => handleSelectChange(selected, 'courseId')}
                      placeholder="Select Course"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="schoolId" className="form-label required">School</label>
                    <Select
                      id="schoolId"
                      options={state.schools.map(school => ({
                        value: school.school_id,
                        label: school.school_name
                      }))}
                      value={state.schools.find(school => school.school_id === state.formData.schoolId) ? {
                        value: state.formData.schoolId,
                        label: state.schools.find(school => school.school_id === state.formData.schoolId).school_name
                      } : null}
                      onChange={(selected) => handleSelectChange(selected, 'schoolId')}
                      placeholder="Select School"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="classId" className="form-label required">Class</label>
                    <Select
                      id="classId"
                      options={state.classes.map(cls => ({
                        value: cls.classId,
                        label: cls.className
                      }))}
                      value={state.classes.find(cls => cls.classId === state.formData.classId) ? {
                        value: state.formData.classId,
                        label: state.classes.find(cls => cls.classId === state.formData.classId).className
                      } : null}
                      onChange={(selected) => handleSelectChange(selected, 'classId')}
                      placeholder="Select Class"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="numberOfCourseContentVisible" className="form-label">Visible Content Count</label>
                    <input
                      type="number"
                      className="form-control no-spinners p-2"
                      id="numberOfCourseContentVisible"
                      name="numberOfCourseContentVisible"
                      value={state.formData.numberOfCourseContentVisible}
                      onChange={handleNumberInputChange}
                      min="0"
                    />
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <label htmlFor="subscriptionStartDate" className="form-label required me-2">Start Date</label>
                    <DatePicker
                      selected={state.formData.subscriptionStartDate}
                      onChange={(date) => handleDateChange(date, 'subscriptionStartDate')}
                      className="form-control p-2"
                      dateFormat="MMMM d, yyyy"
                      minDate={new Date()}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="subscrptionEndDate" className="form-label required me-2">End Date</label>
                    <DatePicker
                      selected={state.formData.subscrptionEndDate}
                      onChange={(date) => handleDateChange(date, 'subscrptionEndDate')}
                      className="form-control p-2"
                      dateFormat="MMMM d, yyyy"
                      minDate={state.formData.subscriptionStartDate}
                    />
                  </div>
                    <div className=" d-md-flex justify-content-center ms-5 ps-4">
                  <div className="col-md-6 mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isPaymentDone"
                        name="isPaymentDone"
                        checked={state.formData.isPaymentDone}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label ms-2" htmlFor="isPaymentDone">
                        Payment Completed
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isTrialExpired"
                        name="isTrialExpired"
                        checked={state.formData.isTrialExpired}
                        onChange={handleInputChange}
                        disabled={state.formData.isPaymentDone}
                      />
                      <label className="form-check-label ms-2" htmlFor="isTrialExpired">
                        Trial Expired
                      </label>
                    </div>
                  </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button type="button" id="closeSubscriptionModal" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}